// Mobile (`<900px`) card for request items. Mirrors every desktop column —
// name, description (collapsible), photos, brand, quantity, weight, volume —
// inside a single vertically stacked card with a thumbnail in the header.
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, IconButton } from '../../../../ui/index.js';
import { useRequestItemPhotoUrl } from '../../photoDownloadResolver.js';
import { formatRequestDensity } from '../../density.js';
import { formatVolume, formatWeight } from '../../format.js';
import { computePackageVolumeM3, computeRowVolumeM3 } from '../../packageCalc.js';
import type { RequestItemPackageView, RequestKind } from '../../types.js';

export interface ItemMobileCardProps {
  requestId: string;
  itemId: string;
  name: string;
  description: string;
  brand: string;
  material: string;
  weight: number | null;
  volume: number | null;
  quantity: number;
  productQuantity: number | null;
  kind: RequestKind;
  photoKeys: string[];
  packages?: ReadonlyArray<RequestItemPackageView>;
  // WHY (#1451): clicking the thumbnail opens the shared PhotoGallery
  //   (owned by ItemsTab) over ALL of this item's photos, starting at
  //   the given index.
  onOpenGallery: (startIndex: number) => void;
}

/**
 * @aiPurpose Mobile card variant for a single request item. Covers the full
 *   desktop column set; description/photos rows render even when empty so the
 *   reader gets a consistent labelled layout.
 * @aiInvariant The kebab action button + thumbnail live in a header flex row;
 *   description occupies its own collapsible row; numeric facts sit in a 2-col
 *   grid. The collapse threshold is 90 chars (matches desktop).
 */
export function ItemMobileCard(props: ItemMobileCardProps) {
  const { t } = useTranslation(['requests', 'common']);
  const [expanded, setExpanded] = useState(false);
  const [packagesExpanded, setPackagesExpanded] = useState(false);
  const canCollapse = props.description.length > 90;
  const photoCount = props.photoKeys.length;
  const packages = props.packages ?? [];
  const hasPackages = packages.length > 0;
  return (
    <article
      aria-label={props.name}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        background: 'var(--bg-1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            fontWeight: 600,
            fontSize: 14,
            color: 'var(--fg-1)',
            wordBreak: 'break-word',
          }}
        >
          {props.name}
        </div>
        <ItemPhotoThumb
          requestId={props.requestId}
          itemId={props.itemId}
          objectKey={props.photoKeys[0] ?? null}
          extraCount={photoCount > 1 ? photoCount - 1 : 0}
          onOpen={() => props.onOpenGallery(0)}
        />
        <IconButton icon="more-vertical" label={t('common:actions.more')} size={28} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, fontSize: 12 }}>
        <span style={{ color: 'var(--fg-3)', fontSize: 11 }}>
          {t('requests:table.description')}
        </span>
        {props.description ? (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, minWidth: 0 }}>
            <div
              style={{
                color: 'var(--fg-1)',
                fontSize: 13,
                lineHeight: 1.35,
                overflow: 'hidden',
                display: expanded ? 'block' : '-webkit-box',
                WebkitLineClamp: expanded ? undefined : 2,
                WebkitBoxOrient: 'vertical',
                wordBreak: 'break-word',
              }}
            >
              {props.description}
            </div>
            {canCollapse && (
              <IconButton
                icon="chevron-down"
                label={t('common:actions.more')}
                size={22}
                onClick={() => setExpanded((v) => !v)}
                style={{ transform: expanded ? 'rotate(180deg)' : 'none', flex: '0 0 auto' }}
              />
            )}
          </div>
        ) : (
          <span style={{ color: 'var(--fg-4)', fontSize: 12 }}>—</span>
        )}
      </div>
      <dl
        style={{
          margin: 0,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '4px 12px',
          fontSize: 12,
        }}
      >
        <ItemFact label={t('requests:table.brand')}>{props.brand || '—'}</ItemFact>
        <ItemFact label={t('requests:table.material')}>{props.material || '—'}</ItemFact>
        <ItemFact label={t('requests:table.productQuantity')}>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{props.productQuantity ?? '—'}</span>
        </ItemFact>
        <ItemFact label={t('requests:table.placesCount')}>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {props.kind === 'CONTAINER' ? '—' : props.quantity}
          </span>
        </ItemFact>
        <ItemFact label={t('requests:common.weight')}>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatWeight(props.weight)}</span>
        </ItemFact>
        <ItemFact label={t('requests:common.volume')}>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatVolume(props.volume)}</span>
        </ItemFact>
        <ItemFact label={t('requests:common.density')}>
          <span data-testid="request-item-density" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatRequestDensity(props.weight, props.volume)}
          </span>
        </ItemFact>
        <ItemFact label={t('requests:table.photos')}>
          {photoCount > 0 ? (
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{photoCount}</span>
          ) : (
            <span style={{ color: 'var(--fg-4)' }}>—</span>
          )}
        </ItemFact>
      </dl>
      {hasPackages && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button
            type="button"
            aria-expanded={packagesExpanded}
            onClick={() => setPackagesExpanded((v) => !v)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: 'var(--fg-2)',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              alignSelf: 'flex-start',
            }}
          >
            <Icon
              name="chevron-down"
              size={14}
              style={{ transform: packagesExpanded ? 'rotate(180deg)' : 'none' }}
            />
            {t('requests:draft.packages.itemTotal')} · {packages.length}
          </button>
          {packagesExpanded && (
            <ul
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              {packages.map((p, idx) => {
                const perPackage = computePackageVolumeM3({
                  widthCm: p.widthCm,
                  depthCm: p.depthCm,
                  heightCm: p.heightCm,
                  weightKg: p.weightKg,
                  count: p.count,
                });
                const rowVolume = computeRowVolumeM3({
                  widthCm: p.widthCm,
                  depthCm: p.depthCm,
                  heightCm: p.heightCm,
                  weightKg: p.weightKg,
                  count: p.count,
                });
                return (
                  <li
                    key={p.id}
                    style={{
                      border: '1px solid var(--border-2)',
                      borderRadius: 'var(--r-1)',
                      padding: 8,
                      background: 'var(--bg-2)',
                      fontSize: 12,
                      color: 'var(--fg-2)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                    }}
                  >
                    <div style={{ fontWeight: 600, color: 'var(--fg-3)' }}>
                      {t('requests:draft.packages.place', { index: idx + 1 })}
                    </div>
                    <div style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {t('requests:draft.packages.mobileSummary', {
                        w: p.widthCm,
                        d: p.depthCm,
                        h: p.heightCm,
                        weight: p.weightKg,
                        count: p.count,
                      })}
                    </div>
                    <div style={{ color: 'var(--fg-3)' }}>
                      {t('requests:draft.packages.perPackageVolume')}:{' '}
                      <strong>{formatPackageM3(perPackage)}</strong> ·{' '}
                      {t('requests:draft.packages.rowVolume')}:{' '}
                      <strong>{formatPackageM3(rowVolume)}</strong>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </article>
  );
}

function formatPackageM3(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0';
  return String(Math.round(value * 10000) / 10000);
}

function ItemFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <dt style={{ color: 'var(--fg-3)', fontSize: 11 }}>{label}</dt>
      <dd style={{ margin: 0, color: 'var(--fg-1)', fontSize: 13, wordBreak: 'break-word' }}>
        {children}
      </dd>
    </div>
  );
}

function ItemPhotoThumb({
  requestId,
  itemId,
  objectKey,
  extraCount,
  onOpen,
}: {
  requestId: string;
  itemId: string;
  objectKey: string | null;
  // WHY (#1451): count beyond the first photo (`photoKeys.length - 1`);
  //   `0` renders exactly as the single-photo thumbnail did before.
  extraCount: number;
  onOpen: () => void;
}) {
  const { t } = useTranslation('requests');
  // WHY (#2140/#2580): item cards and request-list thumbnails share the
  //   issued-key guard and signed-URL flow; snapshots still inject their
  //   frozen resolver through the same hook.
  const downloadUrl = useRequestItemPhotoUrl(requestId, itemId, objectKey);
  if (!objectKey) return null;
  if (!downloadUrl) {
    return (
      <span style={{ color: 'var(--fg-3)', flexShrink: 0 }}>
        <Icon name="image" size={18} />
      </span>
    );
  }
  return (
    <span style={{ position: 'relative', flexShrink: 0, display: 'inline-flex' }}>
      <button
        type="button"
        aria-label={t('table.photos')}
        onClick={onOpen}
        style={{
          width: 48,
          height: 32,
          borderRadius: 'var(--r-1)',
          overflow: 'hidden',
          border: '1px solid var(--border-2)',
          background: 'var(--bg-2)',
          padding: 0,
        }}
      >
        <img
          src={downloadUrl}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </button>
      {extraCount > 0 && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            bottom: -6,
            right: -6,
            minWidth: 18,
            height: 18,
            padding: '0 4px',
            borderRadius: 9,
            background: 'var(--fg-2)',
            color: 'var(--bg-1)',
            fontSize: 10,
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {t('photo.moreBadge', { count: extraCount })}
        </span>
      )}
    </span>
  );
}
